import { autoinject, bindable, observable, computedFrom } from 'aurelia-framework';
import { AccountingRuleApi, IAddRuleAM } from "./accounting-rule-api";
import { ResponseHandler } from '../../utils/response-handler';
import { IAccountingRule, MissingServiceTargetingRule, MissingMunicipalityAllocationRule, IAccountingRuleFolder } from './accounting-rule-models';
import * as moment from 'moment';
import { ScopeService, IScopeAM } from '../scopes/scope-service';
import { AccountingRuleList } from './accounting-rule-list';
import { AppSettings } from '../../utils/appsettings';
import { ResourceService } from '../../utils/resourceService';

@autoinject
export class AccountingRuleView {
	@bindable scopeId: string;
	@bindable fiscalYearId: string;
	@bindable customerId: string;
	@bindable environmentName: string;
	@observable addNewRuleFormOpen = false;

	scope: IScopeAM;
	
	rules: IAccountingRule[];
	folders: IAccountingRuleFolder[];

	missingServiceTargetingRules: MissingServiceTargetingRule[];
	missingMunicipalityAllocationRules: MissingMunicipalityAllocationRule[];
	ruleList: AccountingRuleList;

	constructor(private api: AccountingRuleApi, private responseHandler: ResponseHandler, private scopeService: ScopeService, private resourceService: ResourceService) {
	}

	get userHasReadOnlyAccess(): boolean {
		return !AppSettings.roles.some(r => r === 'GlobalAdministrator' || r === 'GlobalCustomerAdministrator');
	}

	@computedFrom('rules')
	get missesAutomaticTypeRecognitionRule(): boolean {
		return this.rules && this.rules.length && !this.rules.find(r => r.typeName === 'AutomaticTypeRecognition');
	}

	// Only show municipality allocation warnings if not in Production-Helsinki environment
	// This is a hotfix for performance issues in Production-Helsinki environment, where no municipality allocation rules are made
	// and the missing municipality allocation warnings were causing performance issues due to the large number of cost centers in that environment.
	@computedFrom('environmentName')
	get showMunicipalityAllocationWarnings(): boolean {
		return this.environmentName !== "Production-Helsinki";
	}

	async attached() {
		this.scope = await this.scopeService.getScope(this.scopeId);
		await this.initializeScopeFolders();
		this.refreshFolders();
	}

	async initializeScopeFolders() {
		const folders = await this.api.getAccountingRuleFoldersForScope(this.scopeId)
		this.folders = [
			{ id: 'rootFolder', scopeId: this.scopeId, name: this.resourceService.getResource("AccountingRule_Listing_Label_DefaultFolderName"), orderNumber: 0, rules: [], isReadOnly: true },
			...folders
		];
	}

	refreshFolders() {
		this.folders.forEach(folder => {
			const folderId = folder.id === 'rootFolder' ? null : folder.id;
			folder.rules = this.rules.filter(r => r.folderId === folderId);
			folder.rules.sort((a, b) => a.orderNumber - b.orderNumber);
		});
	}

	ruleInserted(rule: IAccountingRule) {
		this.rules.push(rule);
		this.addNewRuleFormOpen = false;
	}

	async ruleDeleted() {
		this.missingServiceTargetingRules = await this.api.getMissingServiceTargetingRules(this.scopeId);
		this.missingMunicipalityAllocationRules = await this.api.getMissingMunicipalityAllocationRules(this.scopeId);
	}
}
